import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import shared from './GarageContent.module.scss';
import classes from './PurchaseCard.module.scss';
import VehicleStrip from './VehicleStrip';
import StatusTimeline from './StatusTimeline';
import MediaGallery from './MediaGallery';
import ArrivalChoice, { BringList } from './ArrivalChoice';
import ProgressTracker from './ProgressTracker';
import ShipTracking from './ShipTracking';
import useFocusedCard from './useFocusedCard';
import {
    carFromSnapshot,
    formatUsd,
    formatDay,
} from '../../services/dealerGarage';
import { buildVehicleTitle } from '../../helper/vehicleTitle';

const CLOSED_STEPS = ['Completed', 'Cancelled'];
const HANDOFF_STEPS = ['Ready for Pickup', 'Delivery Scheduled'];
const SHIPPING_FIELDS = ['shipName', 'pickupPort', 'deliveryPort', 'estimatedPickupDate', 'estimatedArrivalDate'];

// Chip tone per billing.label (computed by the API from the invoices).
const PAYMENT_TONES = {
    'Not invoiced': shared.toneMuted,
    'Awaiting payment': shared.toneWarning,
    'Balance due': shared.toneWarning,
    'Paid in full': shared.toneSuccess,
};

// "Mailed Oct 3, 2026 via UPS", from whichever of the two we have.
const mailedLine = (documents) => {
    const parts = [];
    if (documents.mailedOn) parts.push(formatDay(documents.mailedOn));
    if (documents.carrierLabel) parts.push(`via ${documents.carrierLabel}`);
    return parts.length ? `Mailed ${parts.join(' ')}` : '';
};

/**
 * One dealer-stock purchase: where the car is in its journey, what that means,
 * pickup or delivery, the ship, the paperwork and the photos from our yard
 * when they apply, and the step history.
 *
 * Open purchases start expanded; completed and cancelled ones start collapsed
 * to the summary. A deep link (?purchase=<id>) expands the card it points at.
 *
 * `onHandoff(id, { method, address, notes })` resolves to { ok, message,
 * conflict }; `savedAddress` pre-fills a delivery address.
 */
const PurchaseCard = ({ purchase, focused, savedAddress, onHandoff }) => {
    const closed = CLOSED_STEPS.includes(purchase.status);
    const [expanded, setExpanded] = useState(!closed || focused);
    // The team had already arranged the hand-off when the choice was sent.
    // Kept here: the reload that follows can close the arrival section.
    const [handoffNotice, setHandoffNotice] = useState('');
    const cardRef = useFocusedCard(focused);

    useEffect(() => {
        if (focused) setExpanded(true);
    }, [focused]);

    const cancelled = purchase.status === 'Cancelled';
    const plan = Array.isArray(purchase.plan) ? purchase.plan : [];
    const stepInfo = purchase.stepInfo || null;
    const billing = purchase.billing || null;
    const owing = !!(billing && billing.balance > 0);
    const shipping = purchase.shippingDetail || {};
    const hasShipping = SHIPPING_FIELDS.some((field) => shipping[field]);
    const delivery = purchase.delivery || {};
    const isPickup = purchase.status === 'Ready for Pickup';
    const showHandoff = HANDOFF_STEPS.includes(purchase.status)
        && !!(delivery.address || delivery.scheduledDate || delivery.notes);
    const arrival = purchase.arrival || null;
    // Once the team's own hand-off details are in, they say it all.
    const showArrival = !!arrival && !cancelled && !showHandoff;
    const pickupGuide = arrival?.guide?.pickup || null;
    const documents = purchase.documents || null;
    const mailed = documents ? mailedLine(documents) : '';
    // The API builds carrier links itself; checked again so nothing but
    // https is ever handed to an href.
    const trackingUrl = documents && /^https:\/\//i.test(documents.trackingUrl || '') ? documents.trackingUrl : '';
    const yardImages = Array.isArray(purchase.images) ? purchase.images : [];
    const yardVideos = Array.isArray(purchase.videos) ? purchase.videos : [];
    const hasYardMedia = yardImages.length > 0 || yardVideos.length > 0;
    const eligibleOn = purchase.storage?.eligibleOn || '';
    const invoiceIsLink = /^https:\/\//i.test(purchase.invoice || '');
    const hasFacts = !!(purchase.price || purchase.vin || purchase.invoice || eligibleOn);
    const history = Array.isArray(purchase.statusHistory) ? purchase.statusHistory : [];
    const hasDetails = !!handoffNotice || showArrival || showHandoff || !!documents || hasYardMedia || hasShipping
        || purchase.trackable || hasFacts || !!purchase.notes || history.length > 0;
    const detailsId = `purchase-details-${purchase._id}`;
    const car = carFromSnapshot(purchase);
    const carTitle = (car.make || car.model) ? buildVehicleTitle(car) : (car.title || 'Car');

    const submitHandoff = async (choice) => {
        setHandoffNotice('');
        const result = await onHandoff(purchase._id, choice);
        if (result.conflict) setHandoffNotice(result.message);
        return result;
    };

    let statusTone = shared.toneBrand;
    if (cancelled) statusTone = shared.toneDanger;
    else if (purchase.status === 'Completed') statusTone = shared.toneSuccess;

    return (
        <article ref={cardRef} className={`${shared.card} ${focused ? shared.cardFocused : ''}`}>
            <div className={shared.cardHead}>
                <div>
                    <span className={shared.typeTag}>Purchase</span>
                    <span className={shared.date}>{formatDay(purchase.purchaseDate || purchase.createdAt)}</span>
                </div>
                <span className={`${shared.status} ${statusTone}`}>{purchase.status}</span>
            </div>

            <VehicleStrip record={purchase} listing={false} />

            {billing && (
                <div className={classes.billingRow}>
                    <span className={classes.billingLabel}>Payment</span>
                    {/* "Balance due" says the same as the link next to it, so the
                        link stands in for the chip. */}
                    {!(owing && billing.label === 'Balance due') && (
                        <span className={`${shared.status} ${PAYMENT_TONES[billing.label] || shared.toneMuted}`}>
                            {billing.label}
                        </span>
                    )}
                    {owing && (
                        <Link to={billing.openInvoiceId ? `/garage?tab=billing&invoice=${billing.openInvoiceId}` : "/garage?tab=billing"} className={classes.balanceLink}>
                            Balance due {formatUsd(billing.balance)}
                            <i className="fas fa-chevron-right" aria-hidden="true"></i>
                        </Link>
                    )}
                    {owing && billing.paid > 0 && (
                        <span className={classes.billingNote}>
                            {formatUsd(billing.paid)} of {formatUsd(billing.invoiced)} paid
                        </span>
                    )}
                </div>
            )}

            {cancelled ? (
                <div className={classes.cancelled}>
                    <i className="fas fa-ban" aria-hidden="true"></i>
                    <div>
                        <strong>{stepInfo?.summary || 'This purchase has been cancelled.'}</strong>
                        {stepInfo?.next && <p>{stepInfo.next}</p>}
                    </div>
                </div>
            ) : (
                <>
                    {plan.length > 0 && <ProgressTracker plan={plan} status={purchase.status} />}
                    {stepInfo && (
                        <div className={classes.stepInfo}>
                            <div>
                                <span className={classes.stepInfoLabel}>What this means</span>
                                <p>{stepInfo.summary}</p>
                            </div>
                            {stepInfo.next && (
                                <div>
                                    <span className={classes.stepInfoLabel}>What happens next</span>
                                    <p>{stepInfo.next}</p>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Handed over: the review ask, worded by the API (dealerCopy.REVIEW_ASK). */}
                    {purchase.review && (
                        <div className={classes.reviewAsk}>
                            <strong>{purchase.review.title}</strong>
                            <p>{purchase.review.text}</p>
                            <div className={classes.reviewLinks}>
                                {(purchase.review.links || []).map((link) => (
                                    <a
                                        key={link.site}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${classes.reviewLink} ${link.site === 'facebook' ? classes.reviewLinkFacebook : ''}`}
                                    >
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {expanded && hasDetails && (
                <div id={detailsId}>
                    {handoffNotice && (
                        <p className={classes.handoffNotice} role="status">
                            <i className="fas fa-info-circle" aria-hidden="true"></i>
                            {handoffNotice}
                        </p>
                    )}

                    {showArrival && (
                        <ArrivalChoice purchase={purchase} savedAddress={savedAddress} onSubmit={submitHandoff} />
                    )}

                    {showHandoff && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className={isPickup ? 'fas fa-map-marker-alt' : 'fas fa-truck'} aria-hidden="true"></i>
                                {isPickup ? 'Pickup' : 'Delivery'}
                            </h3>
                            <dl className={shared.meta}>
                                {delivery.address && (
                                    <div className={classes.wide}>
                                        <dt>{isPickup ? 'Pickup location' : 'Delivery address'}</dt>
                                        <dd className={classes.preWrap}>{delivery.address}</dd>
                                    </div>
                                )}
                                {delivery.scheduledDate && (
                                    <div>
                                        <dt>{isPickup ? 'Pickup date' : 'Delivery date'}</dt>
                                        <dd>{formatDay(delivery.scheduledDate)}</dd>
                                    </div>
                                )}
                            </dl>
                            {delivery.notes && <p className={shared.message}>{delivery.notes}</p>}
                            {delivery.customerNotes && (
                                <p className={classes.arrivalNotes}>
                                    <span>Your notes</span>
                                    {delivery.customerNotes}
                                </p>
                            )}
                            {isPickup && pickupGuide && <BringList items={pickupGuide.bring} />}
                        </section>
                    )}

                    {documents && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-envelope-open-text" aria-hidden="true"></i>
                                Documents
                            </h3>
                            {mailed && <p className={classes.docsLead}>{mailed}</p>}
                            {(documents.trackingNumber || documents.contents) && (
                                <dl className={shared.meta}>
                                    {documents.trackingNumber && (
                                        <div>
                                            <dt>Tracking number</dt>
                                            <dd>
                                                {trackingUrl
                                                    ? <a href={trackingUrl} target="_blank" rel="noopener noreferrer">{documents.trackingNumber}</a>
                                                    : documents.trackingNumber}
                                            </dd>
                                        </div>
                                    )}
                                    {documents.contents && (
                                        <div className={classes.wide}>
                                            <dt>What's inside</dt>
                                            <dd className={classes.preWrap}>{documents.contents}</dd>
                                        </div>
                                    )}
                                </dl>
                            )}
                        </section>
                    )}

                    {hasYardMedia && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-camera" aria-hidden="true"></i>
                                Photos from our yard
                            </h3>
                            <MediaGallery images={yardImages} videos={yardVideos} title={carTitle} label="yard" />
                        </section>
                    )}

                    {(hasShipping || purchase.trackable) && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-ship" aria-hidden="true"></i>
                                Shipping
                            </h3>
                            {hasShipping && (
                                <dl className={shared.meta}>
                                    {shipping.shipName && <div><dt>Vessel</dt><dd>{shipping.shipName}</dd></div>}
                                    {shipping.pickupPort && <div><dt>From</dt><dd>{shipping.pickupPort}</dd></div>}
                                    {shipping.deliveryPort && <div><dt>To</dt><dd>{shipping.deliveryPort}</dd></div>}
                                    {shipping.estimatedPickupDate && <div><dt>Departs</dt><dd>{formatDay(shipping.estimatedPickupDate)}</dd></div>}
                                    {shipping.estimatedArrivalDate && <div><dt>Arrives</dt><dd>{formatDay(shipping.estimatedArrivalDate)}</dd></div>}
                                </dl>
                            )}
                            {purchase.trackable && (
                                <ShipTracking trackId={purchase._id} vesselName={shipping.shipName} />
                            )}
                        </section>
                    )}

                    {hasFacts && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-car" aria-hidden="true"></i>
                                Purchase details
                            </h3>
                            <dl className={shared.meta}>
                                {purchase.price ? <div><dt>Agreed price</dt><dd>{formatUsd(purchase.price)}</dd></div> : null}
                                {purchase.vin && <div><dt>VIN</dt><dd>{purchase.vin}</dd></div>}
                                {eligibleOn && <div><dt>Eligible to import</dt><dd>{formatDay(eligibleOn)}</dd></div>}
                                {purchase.invoice && (
                                    <div>
                                        <dt>Invoice</dt>
                                        <dd>
                                            {invoiceIsLink
                                                ? <a href={purchase.invoice} target="_blank" rel="noopener noreferrer">View invoice</a>
                                                : purchase.invoice}
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </section>
                    )}

                    {purchase.notes && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-comment-alt" aria-hidden="true"></i>
                                Notes from our team
                            </h3>
                            <p className={shared.message}>{purchase.notes}</p>
                        </section>
                    )}

                    {history.length > 0 && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-history" aria-hidden="true"></i>
                                Timeline
                            </h3>
                            <StatusTimeline history={history} />
                        </section>
                    )}
                </div>
            )}

            {hasDetails && (
                <button
                    type="button"
                    className={classes.detailsToggle}
                    aria-expanded={expanded}
                    aria-controls={expanded ? detailsId : undefined}
                    onClick={() => setExpanded((open) => !open)}
                >
                    {expanded ? 'Hide details' : 'Show details & timeline'}
                    <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
                </button>
            )}
        </article>
    );
};

export default PurchaseCard;
